import { Image, Pressable, Text, View } from "react-native";
import { Check } from "lucide-react-native";
import { Row } from "@louise/moon-ui";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  DOSE_STATUS_COLORS,
  DOSE_STATUS_ICONS,
  type DoseStatus,
} from "@src/packages/care/medications";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type MedicationForm =
  "pen" | "vial" | "pill" | "suppository" | "supplies";
export type MedicationRowSurface = "standalone" | "nested";
export type MedicationRowAppearance = "default" | "calendar-glass";
export type MedicationRowRightAction =
  "checkbox" | "chevron" | "partnerTag" | "none";
export type MedicationAvailabilityStatus =
  | "on-hand"
  | "in-transit"
  | "pickup-ready"
  | "low-stock"
  | "not-on-hand"
  | "refill-needed"
  | "ordered"
  | "unknown";

const medicationPillIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;
const medicationVialIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;
const medicationPenIllustration =
  require("@src/apps/moon/assets/images/injection-pen.png") as number;
const avatarPlaceholder =
  require("@src/apps/moon/assets/images/avatar-placeholder.png") as number;

function inferMedicationFormFromFormat(format: string | undefined) {
  const normalized = format?.toLowerCase() ?? "";
  if (normalized.includes("pen")) return "pen";
  if (normalized.includes("vial")) return "vial";
  if (normalized.includes("oral") || normalized.includes("pill")) return "pill";
  return null;
}

function getMedicationIllustration(
  form: MedicationForm | undefined,
  format: string | undefined,
) {
  const resolvedForm = form ?? inferMedicationFormFromFormat(format);

  if (resolvedForm === "pen") return medicationPenIllustration;
  if (resolvedForm === "vial") return medicationVialIllustration;
  return medicationPillIllustration;
}

function buildMedicationSubtitle(format: string | undefined, subtitle: string) {
  if (!format) return subtitle;
  if (!subtitle) return format;
  return `${format} · ${subtitle}`;
}

function getAvailabilityTag(status: MedicationAvailabilityStatus | undefined) {
  if (!status || status === "on-hand") {
    return null;
  }
  if (status === "in-transit") {
    return {
      labelKey: "homepage.medication_row.in_transit",
      textColor: "#895D4E",
      backgroundColor: "rgba(137,93,78,0.12)",
    };
  }
  if (status === "pickup-ready") {
    return {
      labelKey: "homepage.medication_row.pickup_ready",
      textColor: "#6B5145",
      backgroundColor: "rgba(137,93,78,0.1)",
    };
  }
  if (status === "low-stock" || status === "refill-needed") {
    return {
      labelKey: "homepage.medication_row.low_stock",
      textColor: "#A35A35",
      backgroundColor: "rgba(163,90,53,0.13)",
    };
  }
  if (status === "not-on-hand") {
    return {
      labelKey: "homepage.medication_row.not_on_hand",
      textColor: "#9E3F26",
      backgroundColor: "rgba(158,63,38,0.13)",
    };
  }
  if (status === "ordered") {
    return {
      labelKey: "homepage.medication_row.ordered",
      textColor: "#6F625A",
      backgroundColor: "rgba(111,98,90,0.1)",
    };
  }
  return null;
}

function PartnerQuietTag({ label }: { label: string }) {
  return (
    <View
      style={{
        borderRadius: 999,
        backgroundColor: "rgba(121,70,51,0.08)",
        borderWidth: 1,
        borderColor: "rgba(121,70,51,0.08)",
        paddingLeft: 4,
        paddingRight: 8,
        paddingVertical: 3,
        maxWidth: 92,
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
      }}
    >
      <Image
        source={avatarPlaceholder}
        resizeMode="cover"
        style={{
          width: 18,
          height: 18,
          borderRadius: 9,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.58)",
        }}
      />
      <Text
        className="font-dm-sans"
        numberOfLines={1}
        style={{
          fontSize: 10.5,
          lineHeight: 13,
          color: EMBER.colors.textLight,
          fontWeight: "800",
        }}
      >
        {label}
      </Text>
    </View>
  );
}

export function MedicationRow({
  name,
  dose,
  format,
  subtitle,
  form,
  availabilityStatus,
  leftLabel,
  time,
  surface = "standalone",
  appearance = "default",
  rightAction = "none",
  completed = false,
  doseStatus,
  isStopped = false,
  partnerName,
  onPress,
  onCheckboxPress,
}: {
  name: string;
  dose?: string;
  format?: string;
  subtitle: string;
  form?: MedicationForm;
  availabilityStatus?: MedicationAvailabilityStatus;
  leftLabel?: string;
  time?: string;
  surface?: MedicationRowSurface;
  appearance?: MedicationRowAppearance;
  rightAction?: MedicationRowRightAction;
  completed?: boolean;
  doseStatus?: DoseStatus;
  isStopped?: boolean;
  partnerName?: string;
  onPress?: () => void;
  onCheckboxPress?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationRow");
  const subtitleText = buildMedicationSubtitle(format, subtitle);
  const illustration = getMedicationIllustration(form, format);
  const isStandalone = surface === "standalone";
  const isCalendarGlass = appearance === "calendar-glass";
  const availabilityTag = getAvailabilityTag(availabilityStatus);
  const leadingLabel = leftLabel ?? time;
  const titleRight =
    dose || availabilityTag ? (
      <>
        {dose ? (
          <View
            style={{
              borderRadius: 6,
              paddingHorizontal: 7,
              paddingVertical: 2,
              backgroundColor: completed
                ? isCalendarGlass
                  ? "rgba(255,247,239,0.08)"
                  : "rgba(107,85,72,0.08)"
                : isCalendarGlass
                  ? "rgba(255,247,239,0.12)"
                  : "rgba(111,98,90,0.1)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10.5,
                lineHeight: 12,
                color: completed
                  ? isCalendarGlass
                    ? "rgba(255,247,239,0.5)"
                    : "rgba(107,85,72,0.48)"
                  : isCalendarGlass
                    ? "rgba(255,247,239,0.82)"
                    : EMBER.colors.textMid,
                fontWeight: "800",
              }}
            >
              {dose}
            </Text>
          </View>
        ) : null}
        {availabilityTag ? (
          <View
            style={{
              borderRadius: 6,
              paddingHorizontal: 7,
              paddingVertical: 2,
              backgroundColor: availabilityTag.backgroundColor,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10.5,
                lineHeight: 12,
                color: completed
                  ? isCalendarGlass
                    ? "rgba(255,247,239,0.5)"
                    : "rgba(107,85,72,0.48)"
                  : isCalendarGlass
                    ? "rgba(255,247,239,0.82)"
                    : availabilityTag.textColor,
                fontWeight: "800",
              }}
            >
              {t(availabilityTag.labelKey)}
            </Text>
          </View>
        ) : null}
      </>
    ) : null;

  const trailing =
    rightAction === "partnerTag" && partnerName ? (
      <View
        style={{
          width: 74,
          alignItems: "flex-end",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <PartnerQuietTag label={partnerName} />
      </View>
    ) : rightAction === "checkbox" ? (
      (() => {
        const statusColor = doseStatus ? DOSE_STATUS_COLORS[doseStatus] : null;
        const StatusIcon = doseStatus ? DOSE_STATUS_ICONS[doseStatus] : Check;
        const filled = Boolean(doseStatus);
        return (
          <Pressable
            accessibilityRole="checkbox"
            accessibilityLabel={t("homepage.review_concept.log_for_a11y", {
              params: { label: name },
            })}
            accessibilityState={{ checked: filled }}
            onPress={(event) => {
              event.stopPropagation?.();
              capturePress(pressLabel(name), "medication_row_checkbox");
              onCheckboxPress?.();
            }}
            style={{
              width: 34,
              alignItems: "flex-end",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                backgroundColor: filled
                  ? (statusColor ?? "transparent")
                  : "transparent",
                borderWidth: filled ? 0 : 1.5,
                borderColor: filled
                  ? (statusColor ?? "transparent")
                  : isCalendarGlass
                    ? "rgba(255,247,239,0.78)"
                    : "rgba(0,0,0,0.12)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {filled ? (
                <StatusIcon size={14} color="#fff" strokeWidth={2.5} />
              ) : null}
            </View>
          </Pressable>
        );
      })()
    ) : null;

  return (
    <Row
      accessibilityRole="button"
      surface={isCalendarGlass ? "plain" : isStandalone ? "glass" : "plain"}
      density={isCalendarGlass || isStandalone ? "default" : "dense"}
      subtitleVariant="meta"
      leadingMeta={leadingLabel}
      leadingMetaWidth={50}
      leadingMetaStyle={{
        color:
          isStopped || completed
            ? isCalendarGlass
              ? "rgba(255,247,239,0.5)"
              : "rgba(107,85,72,0.48)"
            : isCalendarGlass
              ? "rgba(255,247,239,0.62)"
              : EMBER.colors.textLight,
        textDecorationLine: isStopped ? "line-through" : "none",
        textDecorationColor: "rgba(107,85,72,0.5)",
      }}
      leading={
        isCalendarGlass ? undefined : (
          <Image
            source={illustration}
            resizeMode="contain"
            style={{
              width: isStandalone ? 32 : 28,
              height: isStandalone ? 32 : 28,
            }}
          />
        )
      }
      leadingSize={isStandalone ? 34 : 30}
      gap={isCalendarGlass ? 10 : isStandalone ? 12 : 11}
      borderRadius={isCalendarGlass ? 14 : isStandalone ? 18 : 12}
      paddingHorizontal={isCalendarGlass ? 11 : isStandalone ? 16 : 0}
      paddingVertical={isCalendarGlass ? 10 : isStandalone ? 13 : 9}
      style={
        isCalendarGlass
          ? {
              backgroundColor: "rgba(255,247,239,0.1)",
              borderWidth: 1,
              borderColor: "rgba(255,247,239,0.14)",
            }
          : undefined
      }
      muted={completed}
      onPress={onPress}
      action="medication_row"
      title={name}
      titleNumberOfLines={1}
      titleRight={titleRight}
      titleStyle={{
        color:
          isStopped || completed
            ? isCalendarGlass
              ? "rgba(255,247,239,0.54)"
              : "rgba(57,36,29,0.54)"
            : isCalendarGlass
              ? "#FFF7EF"
              : isStandalone
                ? semanticColors.typography.primary
                : EMBER.colors.text,
        textDecorationLine: isStopped ? "line-through" : "none",
        textDecorationColor: "rgba(57,36,29,0.58)",
      }}
      subtitle={subtitleText}
      subtitleNumberOfLines={isStandalone ? undefined : 2}
      subtitleStyle={{
        color:
          isStopped || completed
            ? isCalendarGlass
              ? "rgba(255,247,239,0.5)"
              : "rgba(107,85,72,0.48)"
            : isCalendarGlass
              ? "rgba(255,247,239,0.62)"
              : isStandalone
                ? semanticColors.typography.supporting
                : EMBER.colors.textMid,
        textDecorationLine: isStopped ? "line-through" : "none",
        textDecorationColor: "rgba(107,85,72,0.48)",
      }}
      trailing={trailing}
      chevronColor={isCalendarGlass ? "rgba(255,247,239,0.66)" : undefined}
      chevron={rightAction === "chevron"}
    />
  );
}
